import { PaymentMethod } from "#common/enums";
import { type AddressInputType } from "../SharedAddressInput/constants";

/**
 * Address shape reused from SharedAddressInput. The LLD refers to this as
 * `AddressValue`; the in-repo type is `AddressInputType` (SharedAddressInput),
 * aliased here rather than redefined.
 */
export type AddressValue = AddressInputType;

/**
 * Country-specific local routing identifiers. Verbatim from the LLD §Frontend
 * State Models and Types.
 */
export type LocalRouting = {
  sortCode?: string;
  bsb?: string;
  transitNumber?: string;
  institutionNumber?: string;
};

/**
 * The data the single-page form collects, in the shape `useCreateBankAccount`
 * consumes. All fields optional.
 */
export type BankAccountOnboardingData = {
  country?: string;
  currency?: string;
  swiftBic?: string;
  bankNameIntl?: string;
  accountNumberOrIban?: string;
  localRouting?: LocalRouting;
  ownerName?: string;
  ownerAddress?: AddressInputType;
  notes?: string;
  usAccount?: boolean;
};

/**
 * Workflow mode for the bank account onboarding flow.
 * Mirrors the per-component enum precedent in PayeeWorkflows (PayeeWorkflowModes).
 */
export enum BankAccountOnboardingMode {
  CREATE = "create",
  EDIT = "edit",
}

/**
 * Permissions gating create/edit actions in the workflow.
 */
export type BankAccountPermissions = {
  canCreate: boolean;
  canEdit: boolean;
};

/**
 * Hydration seed for edit mode — an existing bank account passed into the
 * workflow. Verbatim from the LLD §Frontend State Models and Types.
 */
export type ExistingBankAccount = {
  id: string;
  country: string;
  currency: string;
  swiftBic?: string;
  bankNameIntl?: string;
  bankInfoRedacted: string;
  localRouting?: LocalRouting;
  ownerName?: string;
  ownerAddress?: AddressValue;
  notes?: string;
};

/**
 * Minimal local shape for the created bank account returned to `onSuccess`.
 * Matches the LLD `createBankAccount` return selection. Swap to the generated
 * `#graphql/supergraphTypes` type once the mutation ships (LLD §Development
 * Strategy).
 *
 * NOTE: placeholder — remove this note and this local type in favor of the
 * generated type once the `createBankAccount` mutation ships.
 */
export type CreatedBankAccount = {
  id: string;
  bankInfoRedacted: string;
  country: string;
  currency: string;
  swiftBic?: string;
  bankNameIntl?: string;
  paymentMethods: PaymentMethod[];
};

/**
 * Public props for BankAccountOnboardingWorkflow. Verbatim from the LLD §FE-1
 * Public Props.
 *
 * Note: `mode` (CREATE/EDIT) is intentionally absent — this component only
 * implements the create flow. Edit mode is deferred to FF-8339/FF-8340 and
 * will be added then. `initialBankAccount` is retained for entity pre-fill
 * (country hydration) in create mode.
 */
export type BankAccountOnboardingWorkflowProps = {
  /**
   * Controls modal visibility. The component stays mounted while `show` toggles,
   * so `ModalV2` can play its enter/exit animation; the parent keeps it rendered
   * and flips `show` (calling `onExit` to request a close). The form resets to
   * its initial values on every re-open.
   */
  show: boolean;
  entityId?: string;
  arenaId: string;
  usCentricMode: boolean;
  permissions: BankAccountPermissions;
  onSuccess: (bankAccount: CreatedBankAccount) => void;
  onExit: () => void;
  initialBankAccount?: ExistingBankAccount;
};
